/**
 * Client mirror of the migrated-tool set. Only tools served by the new OpsTumbler Java proxy go
 * through Piezo; everything else is legacy and MUST NOT be signed.
 *
 * MUST stay in sync with server/utils/tumbler/toolMigration.ts (MIGRATED_TOOL_PATHS) — that server file is
 * the single source of truth for routing (proxy vs legacy). migratedTools.test.ts enforces the pairing.
 *
 * Not one shared module because server/tsconfig.json sets no rootDir: a module above server/ moves the
 * inferred root and shifts every emitted path, relocating the server entry point. Sharing it needs an
 * explicit rootDir and a check that webpack does not follow it into the browser bundle. A test can import
 * across the boundary because ts-jest emits nothing, so it never forms the input set that decides rootDir.
 */
export const MIGRATED_TOOL_PATHS: ReadonlySet<string> = new Set<string>([
  // Domain Config -- Control Plane
  'aes/domain/cp/describe-elasticsearch-domain',
  'aes/domain/cp/get-elasticsearch-domain-config',
  'aes/domain/cp/get-elasticsearch-version',
  'aes/domain/cp/cs-get-registered-vfis',
  'aes/domain/cp/cs-manage-sensitive-customers',
  'aes/domain/cp/cs-adms-isolate',
  'aes/domain/cp/get-domain-create-datetime',
  'aes/domain/cp/describe-domain-change-progress',
  'aes/domain/bg-completion-time-estimate',
  'aes/domain/get-cell-details',
  'aes/domain/is-feature-available',

  // ES Curl / API Explorer & Node
  'aes/domain/dp/node/es/curl/get',
  'aes/domain/dp/node/es/curl/get-filtered-response',
  'aes/domain/dp/node/es/curl/servertype/explain',
  'aes/domain/dp/node/es/curl/discovery/explain',
  'aes/domain/dp/node/kibana/status',
  'aes/domain/dp/node/shell',
  'aes/domain/dp/node/sdpdagent/ls',

  // AWS Resources, CloudWatch, Logs & Misc
  'aes/domain/dp/get-domain-resources',
  'aes/domain/dp/get-group-resources',
  'aes/domain/dp/grep-es-log',
  'aes/domain/dp/log-redaction-feedback',
  'aes/domain/cw/get-external-cw-metrics',
  'aes/domain/cw/get-sdpds-cw-metrics',
  'aes/domain/cw/list-cw-metrics',
  'aes/domain/cw/list-internal-cw-metrics',
  'aes/domain/cw/get-cx-cw-metrics-table',
  'aes/flint/fetch-emr-job-dashboard',

  // Control plane -- connections & recovery
  'aes/domain/cp/describe-inbound-connections',
  'aes/domain/cp/describe-outbound-connections',
  'aes/domain/cp/recover-domain',

  // Node & component lifecycle. Unlike everything above, these MUTATE the domain.
  'aes/domain/dp/activate-kibana',
  'aes/domain/dp/deactivate-kibana',
  'aes/domain/dp/node/is-os-containerized',
  'aes/domain/dp/node/reboot-instance',
  'aes/domain/dp/node/terminate-instance',
  'aes/domain/dp/node/domain-maintenance/activate',
  'aes/domain/dp/node/domain-maintenance/deactivate',
  'aes/domain/dp/node/domain-maintenance/preactivate',
  'aes/domain/dp/node/es/activate',
  'aes/domain/dp/node/es/deactivate',
  'aes/domain/dp/node/es/preactivate',
  'aes/domain/dp/node/kibana/activate',
  'aes/domain/dp/node/kibana/deactivate',
  'aes/domain/dp/node/sdpdagent/activate-agent',
  'aes/domain/dp/node/sdpdagent/deactivate-agent',
  'aes/domain/dp/node/sdpdagent/install-docker',
  'aes/domain/dp/node/sdpdagent/ping-agent',
  'aes/domain/dp/node/sdpdagent/update-agent',
]);

/**
 * Migrated tools the proxy serves without Piezo enforcement: they keep the proxy route but must not be
 * signed. MUST stay in sync with the same set in server/utils/tumbler/toolMigration.ts.
 */
export const PIEZO_DISABLED_TOOL_PATHS: ReadonlySet<string> = new Set<string>([
  'aes/domain/cw/get-external-cw-metrics',
  'aes/domain/cw/get-sdpds-cw-metrics',
  'aes/domain/cw/list-cw-metrics',
  'aes/domain/cw/list-internal-cw-metrics',
  'aes/domain/cw/get-cx-cw-metrics-table',
]);

/** True if the tool is served by the new proxy. Routing only — see isToolPiezoRequired for signing. */
export function isToolMigrated(toolId: string): boolean {
  return MIGRATED_TOOL_PATHS.has(toolId);
}

/**
 * True if this tool needs a browser-created operator artifact. One predicate behind both the decision to
 * sign (createToolPiezoArtifact) and to fail an unsigned operation closed (piezoLink), so they agree.
 */
export function isToolPiezoRequired(toolId: string): boolean {
  return isToolMigrated(toolId) && !PIEZO_DISABLED_TOOL_PATHS.has(toolId);
}
